import React, { useEffect, useState, useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Modal from "@material-ui/core/Modal";
import {
  CircularProgress,
  TextField,
  FormGroup,
  FormControlLabel,
  Switch,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import MainContext from "../../context/main/mainContext";
import getMemberCount from "../../utilities/getMemberCount";
import planLookup from "../../utilities/planLookup";
import useCommonMemberAndTeamAction from "../../hooks/useCommonMemberAndTeamActions";
import IntegrationsSection from "../../components/IntegrationsSection";
import StripeAddPaymentModal from "../../checkout/components/StripeAddPaymentModal";
import ExtendTrialButton from "./ExtendTrialButton";
import dates from "../../utilities/dates";
import { extractSubItemsFromTeam } from "../../utilities/extractSubItemsFromTeam";
import { newBillingPriceConfig } from "../../checkout/config";
import MarketingEmailSettings from "./MarketingEmailSettings";

function getModalStyle() {
  return {
    position: "absolute",
    left: "50%",
    top: "50%",
    transform: "translate(-50%, -50%)",
    width: "65%",
    display: "block",
    "overflow-y": "auto",
    "max-height": "80%",
  };
}

const useStyles = makeStyles((theme) => ({
  paper: {
    position: "absolute",
    width: 400,
    backgroundColor: theme.palette.background.paper,
    border: "2px solid #000",
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
}));

export default function TeamModal(props) {
  const mainContext = useContext(MainContext);
  const {
    snackbarAction,
    user,
    getTeamById,
    team,
    setMemberAdmin,
    getPriceConfig,
    price_config,
    changePlan,
    addTeamOrder,
    isProd,
    getTeamIntegrationConnections,
    integrationConnections,
    getTeamSalesforceData,
    teamSalesforceData,
    getTeamCrmExportLogs,
    crmExportLogs,
    getCustomerPaymentMethods,
    configureSso,
  } = mainContext;

  const {
    isTextFollowUp,
    isEmailFollowUp,
    email,
    editEmailOpen,
    handleSetEmailOpen,
    handleEmailChange,
    isSendWeeklyReminder,
    isImportEnrichmentEnabled,
    isCommonActionLoading,
    handleUpdateTeam,
  } = useCommonMemberAndTeamAction("team");
  const classes = useStyles();
  const [modalStyle] = React.useState(getModalStyle);

  const [selectedPlanInterval, setSelectedPlanInterval] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [internalDemoTeam, setInternalDemoTeam] = useState({});
  const [isPriceMonthly, setIsPriceMonthly] = useState(false);
  const [adminControlsExpanded, setAdminControlsExpanded] = useState(false);
  const [integrationsExpanded, setIntegrationsExpanded] = useState(false);
  const [ssoExpanded, setSsoExpanded] = useState(false);
  const [ssoProvider, setSsoProvider] = useState("azure");
  const [ssoMetadataMode, setSsoMetadataMode] = useState("url"); // "url" | "xml"
  const [ssoMetadataUrl, setSsoMetadataUrl] = useState("");
  const [ssoMetadataXml, setSsoMetadataXml] = useState("");
  const [ssoDomains, setSsoDomains] = useState([]);
  const [ssoLoading, setSsoLoading] = useState(false);
  const [isIntegrationsLoading, setIsIntegrationsLoading] = useState(false);
  const [isCrmExportLogsLoading, setIsCrmExportLogsLoading] = useState(false);
  const [openStripePaymentModal, setOpenStripePaymentModal] = useState(false);
  const [teamSubscriptionFormValues, setTeamSubscriptionFormValues] = useState({
    stripeSubscription: "",
    enterprisePlan: "",
  });
  const [totalSeatsCount, setTotalSeatsCount] = useState(0);

  const [stripePromise, setStripePromise] = useState(null);

  useEffect(() => {
    // These keys are public
    loadStripe(
      isProd
        ? "pk_live_51ISRsnJqkGKmOFO6LLM2GcznWFLHS40RpqGm5N58il5MgKyT3XbfMw0r3jgbpGRcyq24rtr3YrK8Bbd4DaJTO7Pm00dmW3rNlq"
        : "pk_test_51ISRsnJqkGKmOFO6YXwekNU7nltG5qZLswJWgSyQnC78FFt8d5ZxveEdy9bqmVbVPMkxAjeqdc17Wy7sCxYlAPnK00eb2dGR0l",
    ).then((res) => {
      setStripePromise(res);
    });
  }, []);

  useEffect(() => {
    const { seatsItem, freeSeatsItem } = extractSubItemsFromTeam(team);
    const { interval } = seatsItem?.price?.recurring || {};
    const { interval_count: intervalCount } = seatsItem?.price?.recurring || {};

    setSelectedPlanInterval(
      newBillingPriceConfig.find(
        (config) => config.interval === interval && config.intervalCount === intervalCount,
      ),
    );
    setIsPriceMonthly(interval === "month" && intervalCount === 1);
    setTotalSeatsCount(
      Math.max(
        (seatsItem?.quantity ?? 0) + (freeSeatsItem?.quantity ?? 0),
        subscription?.metadata?.PaidProfiles ?? 0,
        seatsItem?.metadata?.InitialQuantity ?? 0,
      ),
    );
  }, [team?.stripeCustomer]);

  useEffect(() => {
    if (team?.stripeCustomer?.id) {
      getCustomerPaymentMethods(
        {
          stripeCustomerId: team?.stripeCustomer.id,
        },
        (err) => {
          if (err) {
            alert(err);
          }
        },
      );
    }
  }, [team?.stripeCustomer]);

  const existingSsoDomainData = Array.isArray(team?.team?.sso_domain_data)
    ? team?.team?.sso_domain_data
    : team?.team?.sso_domain_data
      ? [team.team.sso_domain_data]
      : [];
  const existingSsoSamlData = Array.isArray(team?.team?.sso_saml_data)
    ? team?.team?.sso_saml_data?.[0]
    : team?.team?.sso_saml_data || null;
  const hasExistingSso = existingSsoDomainData.length > 0 || !!existingSsoSamlData;

  useEffect(() => {
    const existingDomains = existingSsoDomainData
      .map((row) => row?.domain)
      .filter(Boolean);
    const derivedProvider = existingSsoSamlData?.isMicrosoft ? "azure" : "other";

    setSsoDomains(existingDomains);

    if (existingSsoSamlData?.xmlMetadata) {
      setSsoMetadataMode("xml");
      setSsoMetadataXml(existingSsoSamlData.xmlMetadata);
      setSsoMetadataUrl("");
    } else {
      setSsoMetadataMode("url");
      setSsoMetadataUrl(existingSsoSamlData?.xmlMetadataUrl || "");
      setSsoMetadataXml("");
    }

    if (existingSsoSamlData) {
      setSsoProvider(derivedProvider);
    }
  }, [team?.team?.id]);

  const handleSsoSubmit = async () => {
    if (hasExistingSso) {
      const proceed = window.confirm(
        "This team already has an SSO configuration. Saving will replace the existing provider, domains, and metadata. Continue?",
      );
      if (!proceed) return;
    }

    // Normalize XML that was copied from a JSON context:
    // 1. strip surrounding whitespace
    // 2. strip any wrapping " or ' characters
    // 3. unescape JSON-escaped quotes (\" → ") and double-backslashes (\\ → \)
    const cleanXml = ssoMetadataXml
      .trim()
      .replace(/^['"]+|['"]+$/g, "")
      .replace(/\\"/g, "\"")
      .replace(/\\\\/g, "\\")
      .trim();

    setSsoLoading(true);
    try {
      await configureSso(team?.team?.id, {
        provider: ssoProvider,
        ...(ssoMetadataMode === "url"
          ? { metadataUrl: ssoMetadataUrl.trim() }
          : { metadataXml: cleanXml }),
        domains: ssoDomains,
      });
    } finally {
      setSsoLoading(false);
    }
  };

  useEffect(() => {
    setTeamSubscriptionFormValues({
      stripeSubscription: team?.team?.stripeSubscription || "",
      enterprisePlan: team?.team?.enterprisePlan || "",
    });

    (async () => {
      if (!team?.team?.id) return;
      await getTeamSalesforceData(team?.team?.id, () => { });
    })();
  }, [team?.team]);

  const handleSubscriptionFormChange = (e) => {
    const { name, value } = e.target;
    setTeamSubscriptionFormValues({
      ...teamSubscriptionFormValues,
      [name]: value,
    });
  };

  const handleSubscriptionFormSubmit = async (e) => {
    e.preventDefault();
    const isConfirmed = window.confirm(
      "Are you sure you want to update this info? Incorrect updates may cause issues",
    );

    if (!isConfirmed) return;
    setIsLoading(true);

    try {
      await handleUpdateTeam(teamSubscriptionFormValues);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const internalDemoTeam = team?.team?.internalDemoTeams?.find(
      (demoTeam) => !demoTeam.isDeleted && !demoTeam.hasPaid && !demoTeam.isExpired,
    );

    const mostRecentInternalDemoTeams = team?.team?.internalDemoTeams.sort((a, b) => {
      const aDate = new Date(a.createdAt);
      const bDate = new Date(b.createdAt);

      return bDate - aDate;
    });

    setInternalDemoTeam({
      active: internalDemoTeam,
      mostRecent: mostRecentInternalDemoTeams?.length ? mostRecentInternalDemoTeams[0] : null,
    });
  }, [team]);

  useEffect(() => {
    setIsLoading(true);

    getTeamById(props.teamId, async (err) => {
      if (!props.teamId) return;
      if (err) {
        alert(err);
      }

      // Get Stripe customer data if team has stripeCustomer ID
      if (team?.team?.stripeCustomer) {
        await getCustomerPaymentMethods(
          {
            stripeCustomerId: team.team.stripeCustomer,
          },
          (err) => {
            if (err) {
              console.error("Error fetching payment methods:", err);
            }
          },
        );
      }

      setIsLoading(false);
    });
  }, [props.teamId]);

  const handleSearchCrmExportLogs = async (teamId, searchQuery) => {
    setIsCrmExportLogsLoading(true);
    await getTeamCrmExportLogs(teamId, searchQuery, () => {
      setIsCrmExportLogsLoading(false);
    });
  };

  const makeAdmin = (userId, teamId, isAdmin) => {
    setIsLoading(true);
    setMemberAdmin(userId, teamId, isAdmin, (err) => {
      if (err) {
        alert(err);
      }
      // Refresh
      // Lookup Team by ID
      getTeamById(props.teamId, (err2) => {
        if (err2) {
          alert(err2);
        }
        setIsLoading(false);
      });
    });
  };

  // Get price config
  useEffect(() => {
    if (!price_config) {
      getPriceConfig((err) => {
        if (err) {
          alert(err);
        }
      });
    }
  }, []);

  const [customDevicesOrdered, setCustomDevicesOrdered] = useState(0);
  const [standardDevicesOrdered, setStandardDevicesOrdered] = useState(0);

  useEffect(() => {
    if (!team?.team?.orders) return;
    const orders = team?.team?.orders;

    const customDevices = orders.reduce((prev, current) => prev + current.customDevices, 0);
    const standardDevices = orders.reduce((prev, current) => prev + current.standardDevices, 0);

    setCustomDevicesOrdered(customDevices);
    setStandardDevicesOrdered(standardDevices);
  }, [team?.team?.orders]);

  const [isAddOrderScreen, setIsAddOrderScreen] = useState(false);

  // Inputs to add order
  const [shopifyOrderId, setShopifyOrderId] = useState("");
  const [fulfillOrderId, setFulfillOrderId] = useState("");
  const [newStandardDevices, setNewStandardDevices] = useState(0);
  const [newCustomDevices, setNewCustomDevices] = useState(0);

  const addOrder = () => {
    setIsLoading(true);

    const payload = {
      teamId: team.team.id,
      shopifyOrderId,
      fulfillOrderId,
      newStandardDevices,
      newCustomDevices,
    };

    addTeamOrder(payload, (err) => {
      if (err) {
        alert(err);
      } else {
        setShopifyOrderId("");
        setFulfillOrderId("");
        setNewStandardDevices(0);
        setNewCustomDevices(0);
        setIsAddOrderScreen(false);
      }

      // Refresh
      getTeamById(props.teamId, (err2) => {
        if (err2) {
          alert(err2);
        }
        setIsLoading(false);
      });
    });
  };

  const subscription = team?.stripeCustomer?.subscriptions?.data.find(
    ({ id }) => id === team?.team?.stripeSubscription?.split("%")[1],
  );

  const paidProfiles = subscription?.metadata?.PaidProfiles;
  const hasActiveSubscription = !!subscription;

  // Make links for env
  const [stripeBaseLink, setStripeBaseLink] = useState("");

  useEffect(() => {
    if (isProd) {
      setStripeBaseLink("https://dashboard.stripe.com/");
    } else {
      setStripeBaseLink("https://dashboard.stripe.com/test/");
    }
  }, [isProd]);

  const stripeOptions = {
    mode: "setup",
    currency: "usd",
    paymentMethodTypes: ["card"],
    disableLink: true,
  };

  const body = (
    <div>
      {stripePromise && (
        <Elements stripe={stripePromise} options={stripeOptions}>
          <StripeAddPaymentModal
            stripe={stripePromise}
            stripeCustomerId={team?.team?.stripeCustomer || team?.stripeCustomer?.id}
            open={openStripePaymentModal}
            closeModal={() => setOpenStripePaymentModal(false)}
          />
        </Elements>
      )}
      <h2 id="simple-modal-title">Team ID: #{team?.team?.id}</h2>

      <div
        style={{
          border: "1px solid gray",
          margin: "1rem 0",
          padding: "1rem",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <div>
            {editEmailOpen ? (
              <TextField
                required="true"
                variant="outlined"
                value={email}
                type="text"
                style={{ width: 400 }}
                label={"Email"}
                onChange={(e) => handleEmailChange(e.target.value)}
                disabled={isCommonActionLoading}
              />
            ) : (
              <p id="simple-modal-description">Email: {email}</p>
            )}

            <p>Plan Level: {planLookup(team?.team, price_config)}</p>
            <p>Member Count: {getMemberCount(team, totalSeatsCount)}</p>
            <p>
              Stripe ID: {team?.team?.stripeCustomer}{" "}
              <span>
                <a
                  href={`${stripeBaseLink}customers/${team?.team?.stripeCustomer}`}
                  rel="noreferrer"
                  target="_blank"
                >
                  | View in Stripe
                </a>
              </span>
            </p>
            <p>Account Executive: {teamSalesforceData?.accountExecutive.email || "None"}</p>
            <p>Customer Success: {teamSalesforceData?.accountCsm.email || "None"}</p>
          </div>

          <fieldset
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
              border: 0,
              padding: "1rem 0",
              margin: 0,
            }}
          >
            <div className="buttonContainer" style={{ margin: 0 }} onClick={handleSetEmailOpen}>
              {editEmailOpen ? "Cancel" : "Edit Email"}
            </div>

            {editEmailOpen && (
              <button
                className="buttonContainer"
                style={{ margin: 0 }}
                onClick={() => {
                  handleUpdateTeam({ email });
                  handleSetEmailOpen(false);
                }}
                disabled={isCommonActionLoading}
              >
                Save
              </button>
            )}
          </fieldset>
        </div>

        <div style={{ marginTop: "1rem" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "1rem",
              marginBottom: "8px",
            }}
          >
            {user.can_access_sales && (
              <button
                onClick={isLoading ? null : (e) => changePlan(team?.team?.id)}
                disabled={isLoading}
                style={{
                  backgroundColor: "black",
                  color: "white",
                  border: "1px solid #e0e0e0",
                  borderRadius: "4px",
                  padding: "4px 8px",
                  cursor: "pointer",
                  fontSize: "12px",
                }}
              >
                {isLoading ? (
                  <CircularProgress color="secondary" size={15} />
                ) : team?.stripeCustomer?.subscriptions?.data.length < 1
                  && internalDemoTeam.active ? (
                  "Update Trial/Quote"
                ) : (
                  "Update Subscription"
                )}
              </button>
            )}
            <button
              onClick={() => setAdminControlsExpanded(!adminControlsExpanded)}
              style={{
                color: "black",
                border: "1px solid #e0e0e0",
                borderRadius: "4px",
                padding: "4px 8px",
                cursor: "pointer",
                fontSize: "12px",
              }}
            >
              {adminControlsExpanded ? "Collapse Admin Controls" : "Expand Admin Controls"}
            </button>
            <button
              onClick={async () => {
                const newExpandedState = !integrationsExpanded;
                setIntegrationsExpanded(newExpandedState);
                if (newExpandedState) {
                  setIsIntegrationsLoading(true);
                  await Promise.all([
                    getTeamIntegrationConnections(team?.team?.id, () => {
                      setIsIntegrationsLoading(false);
                    }),
                    getTeamCrmExportLogs(team?.team?.id, "", () => {
                      setIsCrmExportLogsLoading(false);
                    }),
                  ]);
                }
              }}
              style={{
                backgroundColor: "white",
                color: "black",
                border: "1px solid #e0e0e0",
                borderRadius: "4px",
                padding: "4px 8px",
                cursor: "pointer",
                fontSize: "12px",
              }}
            >
              {integrationsExpanded ? "Collapse Integrations" : "Expand Integrations"}
            </button>
            {user.can_access_admin && (
              <button
                onClick={() => setSsoExpanded(!ssoExpanded)}
                style={{
                  backgroundColor: "white",
                  color: "black",
                  border: "1px solid #e0e0e0",
                  borderRadius: "4px",
                  padding: "4px 8px",
                  cursor: "pointer",
                  fontSize: "12px",
                }}
              >
                {ssoExpanded ? "Collapse SSO Setup" : "SSO Setup"}
              </button>
            )}
          </div>

          <div style={{ width: adminControlsExpanded && !integrationsExpanded ? "60%" : "100%" }}>
            {adminControlsExpanded && user.can_access_admin && (
              <>
                <h4 style={{ marginBottom: "0.5rem" }}>Feature Flags</h4>
                <div
                  style={{
                    border: "1px solid #e0e0e0",
                    borderRadius: "4px",
                    padding: "0.75rem",
                    backgroundColor: "#f9f9f9",
                  }}
                >
                  <FormGroup row style={{ alignItems: "center" }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isTextFollowUp}
                          onChange={(e) => handleUpdateTeam({ textFollowUp: e.target.checked })}
                          name="textFollowUp"
                          inputProps={{ "aria-label": "secondary checkbox" }}
                          disabled={isCommonActionLoading}
                        />
                      }
                      label={"Text Follow ups"}
                    />
                  </FormGroup>
                  <FormGroup row style={{ alignItems: "center" }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isEmailFollowUp}
                          onChange={(e) => handleUpdateTeam({ emailFollowUp: e.target.checked })}
                          name="emailFollowUp"
                          inputProps={{ "aria-label": "secondary checkbox" }}
                          disabled={isCommonActionLoading}
                        />
                      }
                      label={"Email Follow ups"}
                    />
                  </FormGroup>
                  <FormGroup row style={{ alignItems: "center" }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isSendWeeklyReminder}
                          onChange={(e) => handleUpdateTeam({ sendWeeklyInviteReminders: e.target.checked })
                          }
                          name="sendWeeklyReminder"
                          inputProps={{ "aria-label": "secondary checkbox" }}
                          disabled={isCommonActionLoading}
                        />
                      }
                      label={"Weekly Reminders"}
                    />
                  </FormGroup>
                  <FormGroup row style={{ alignItems: "center" }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isImportEnrichmentEnabled}
                          onChange={(e) => handleUpdateTeam({ isImportEnrichmentEnabled: e.target.checked })
                          }
                          name="isImportEnrichmentEnabled"
                          inputProps={{ "aria-label": "secondary checkbox" }}
                          disabled={isCommonActionLoading}
                        />
                      }
                      label={"List Enrichment"}
                    />
                  </FormGroup>
                </div>
                <div>
                  <h4 style={{ marginTop: "1rem" }}>Marketing Email Settings</h4>
                  <MarketingEmailSettings team={team} setIsLoading={setIsLoading} />
                </div>

                <h4 style={{ marginTop: "1rem", marginBottom: "0.5rem" }}>Subscription Settings</h4>
                <div
                  style={{
                    border: "1px solid #e0e0e0",
                    borderRadius: "4px",
                    padding: "0.75rem",
                    backgroundColor: "#f9f9f9",
                    marginBottom: "1rem",
                  }}
                >
                  <form onSubmit={handleSubscriptionFormSubmit}>
                    <div style={{ marginBottom: "0.5rem" }}>
                      <TextField
                        fullWidth
                        label="Stripe Subscription"
                        variant="outlined"
                        size="small"
                        name="stripeSubscription"
                        value={teamSubscriptionFormValues.stripeSubscription || ""}
                        onChange={handleSubscriptionFormChange}
                        disabled={isCommonActionLoading}
                        helperText="Enter the stripe subscription ID (format: subItem%sub)"
                      />
                    </div>
                    <div style={{ marginBottom: "0.5rem" }}>
                      <TextField
                        fullWidth
                        label="Enterprise Plan"
                        variant="outlined"
                        size="small"
                        name="enterprisePlan"
                        value={teamSubscriptionFormValues.enterprisePlan || ""}
                        onChange={handleSubscriptionFormChange}
                        disabled={isCommonActionLoading}
                        helperText="Enterprise plan level (e.g. 60, 50)"
                      />
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        marginTop: "0.5rem",
                      }}
                    >
                      <button
                        type="submit"
                        style={{
                          background: "#2196f3",
                          color: "white",
                          border: "none",
                          borderRadius: "4px",
                          padding: "6px 16px",
                          cursor: "pointer",
                          fontSize: "14px",
                        }}
                        disabled={isCommonActionLoading}
                      >
                        Save
                      </button>
                    </div>
                  </form>
                </div>
              </>
            )}
          </div>

          {integrationsExpanded && (
            <IntegrationsSection
              integrationConnections={integrationConnections}
              crmExportLogs={crmExportLogs}
              isIntegrationsLoading={isIntegrationsLoading}
              isCrmExportLogsLoading={isCrmExportLogsLoading}
              onSearchCrmExportLogs={handleSearchCrmExportLogs}
              entityId={team?.team?.id}
            />
          )}

          {ssoExpanded && user.can_access_admin && (
            <div
              style={{
                border: "1px solid #e0e0e0",
                borderRadius: "4px",
                padding: "0.75rem",
                backgroundColor: "#f9f9f9",
                marginTop: "1rem",
              }}
            >
              <h4 style={{ marginTop: 0, marginBottom: "0.5rem" }}>SSO Configuration</h4>

              {hasExistingSso && (
                <div
                  style={{
                    border: "1px solid #f0c36d",
                    backgroundColor: "#fff8e1",
                    borderRadius: "4px",
                    padding: "0.5rem 0.75rem",
                    marginBottom: "0.75rem",
                    fontSize: "13px",
                  }}
                >
                  <b>Existing SSO config found.</b> Saving will replace the current
                  provider, domains, and metadata.
                  <div style={{ marginTop: "0.5rem" }}>
                    <div>
                      Current provider:{" "}
                      <b>{existingSsoSamlData?.isMicrosoft ? "Azure AD" : "Okta / Other"}</b>
                    </div>
                    <div>
                      Current domains:{" "}
                      <b>
                        {existingSsoDomainData
                          .map((row) => row?.domain)
                          .filter(Boolean)
                          .join(", ") || "(none)"}
                      </b>
                    </div>
                    {existingSsoSamlData?.xmlMetadataUrl && (
                      <div>
                        Metadata URL: <b>{existingSsoSamlData.xmlMetadataUrl}</b>
                      </div>
                    )}
                    {existingSsoSamlData?.xmlMetadata && (
                      <div>
                        Metadata: <b>XML stored</b>{" "}
                        <span style={{ fontFamily: "monospace", fontSize: "11px", color: "#666" }}>
                          ({existingSsoSamlData.xmlMetadata.slice(0, 60)}…)
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div style={{ marginBottom: "0.75rem" }}>
                <TextField
                  select
                  fullWidth
                  SelectProps={{ native: true }}
                  label="Provider"
                  variant="outlined"
                  size="small"
                  value={ssoProvider}
                  onChange={(e) => setSsoProvider(e.target.value)}
                  disabled={ssoLoading}
                >
                  <option value="azure">Azure AD</option>
                  <option value="okta">Okta</option>
                  <option value="other">Other</option>
                </TextField>
              </div>

              <div style={{ marginBottom: "0.75rem" }}>
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={ssoDomains}
                  onChange={(e, newValue) => setSsoDomains(newValue)}
                  disabled={ssoLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Domains"
                      placeholder="e.g. company.com"
                      variant="outlined"
                      size="small"
                      helperText="Press Enter after each domain"
                    />
                  )}
                />
              </div>

              {/* Metadata mode toggle */}
              <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.75rem" }}>
                {["url", "xml"].map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => setSsoMetadataMode(mode)}
                    disabled={ssoLoading}
                    style={{
                      padding: "4px 12px",
                      fontSize: "12px",
                      borderRadius: "4px",
                      cursor: ssoLoading ? "not-allowed" : "pointer",
                      border: "1px solid #2196f3",
                      background: ssoMetadataMode === mode ? "#2196f3" : "white",
                      color: ssoMetadataMode === mode ? "white" : "#2196f3",
                      fontWeight: ssoMetadataMode === mode ? 600 : 400,
                    }}
                  >
                    {mode === "url" ? "Metadata URL" : "Metadata XML"}
                  </button>
                ))}
              </div>

              {ssoMetadataMode === "url" ? (
                <div style={{ marginBottom: "0.75rem" }}>
                  <TextField
                    fullWidth
                    label="Federation Metadata URL"
                    variant="outlined"
                    size="small"
                    value={ssoMetadataUrl}
                    onChange={(e) => setSsoMetadataUrl(e.target.value)}
                    placeholder="https://login.microsoftonline.com/…/federationmetadata/2007-06/federationmetadata.xml"
                    disabled={ssoLoading}
                  />
                </div>
              ) : (
                <div style={{ marginBottom: "0.75rem" }}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={6}
                    maxRows={12}
                    label="Federation Metadata XML"
                    variant="outlined"
                    size="small"
                    value={ssoMetadataXml}
                    onChange={(e) => setSsoMetadataXml(e.target.value)}
                    placeholder={"<EntityDescriptor entityID=\"https://…\" xmlns=\"urn:oasis:names:tc:SAML:2.0:metadata\">…</EntityDescriptor>"}
                    disabled={ssoLoading}
                    inputProps={{ style: { fontFamily: "monospace", fontSize: "12px" } }}
                    helperText="Paste the full XML from your IdP's federation metadata download"
                  />
                </div>
              )}

              {(() => {
                const metadataFilled = ssoMetadataMode === "url"
                  ? !!ssoMetadataUrl.trim()
                  : !!ssoMetadataXml.trim();
                const isDisabled = !metadataFilled || ssoDomains.length === 0 || ssoLoading;
                return (
                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <button
                      type="button"
                      onClick={handleSsoSubmit}
                      disabled={isDisabled}
                      style={{
                        background: isDisabled ? "#b0bec5" : "#2196f3",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        padding: "6px 16px",
                        cursor: isDisabled ? "not-allowed" : "pointer",
                        fontSize: "14px",
                      }}
                    >
                      {ssoLoading ? "Saving..." : "Save SSO Config"}
                    </button>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      </div>

      {(hasActiveSubscription || internalDemoTeam.active?.trialExpiresAtUtc) && (
        <div
          style={{
            border: "1px solid gray",
            margin: "1rem 0",
            padding: "1rem",
          }}
        >
          <h3 style={{ marginTop: 0 }}>Subscription Details</h3>
          {!hasActiveSubscription && internalDemoTeam.active?.trialExpiresAtUtc ? (
            <div>
              <p>On a free trial with no active subscription</p>
              {internalDemoTeam.active?.trialExpiresAtUtc && !isPriceMonthly && (
                <ExtendTrialButton
                  internalDemoTeam={internalDemoTeam.active}
                  setIsLoading={setIsLoading}
                />
              )}
            </div>
          ) : (
            team?.stripeCustomer?.subscriptions?.data?.map((subscription, index) => (
              <div
                key={subscription.id}
                style={
                  index > 0
                    ? { marginTop: "1rem", paddingTop: "1rem", borderTop: "1px solid #e0e0e0" }
                    : {}
                }
              >
                <p>
                  Subscription ID: {subscription.id}{" "}
                  <span>
                    <a
                      href={`${stripeBaseLink}subscriptions/${subscription.id}`}
                      rel="noreferrer"
                      target="_blank"
                    >
                      View in Stripe
                    </a>
                  </span>
                </p>
                <p>Status: {subscription.status}</p>
                <p>
                  Current Period Start:{" "}
                  {new Date(subscription.current_period_start * 1000).toLocaleDateString()}
                </p>
                <p>
                  Current Period End:{" "}
                  {new Date(subscription.current_period_end * 1000).toLocaleDateString()}
                </p>
                <p>
                  Total Subscription Amount: $
                  {subscription.items.data.reduce(
                    (acc, item) => (item?.price?.unit_amount || 0) * item.quantity + acc,
                    0,
                  ) / 100}
                </p>
                <p>
                  Cycle: <b>{isPriceMonthly ? "month" : "year"}ly</b>
                </p>
                {internalDemoTeam?.active?.trialExpiresAtUtc
                  && subscription.items.data[0].price.recurring.interval === "year" && (
                  <ExtendTrialButton
                    internalDemoTeam={internalDemoTeam?.active}
                    setIsLoading={setIsLoading}
                  />
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* Orders */}
      <div
        style={{
          border: "1px solid gray",
          margin: "1rem 0",
          padding: "1rem",
        }}
      >
        <h3 style={{ marginTop: 0 }}>Orders</h3>
        <div>
          <p>
            Customs Ordered: <b>{customDevicesOrdered}</b>
          </p>
          <p>
            Standards Ordered: <b>{standardDevicesOrdered}</b>
          </p>
          {user.can_access_ops && (
            <div
              className="buttonContainerAlt"
              onClick={isLoading ? null : (e) => setIsAddOrderScreen(true)}
              style={{ width: "fit-content" }}
            >
              Add Order
            </div>
          )}
        </div>
      </div>

      {team?.team?.memberTeams?.length > 0 && (
        <div
          style={{
            border: "1px solid gray",
            margin: "1rem 0",
            padding: "1rem",
          }}
        >
          <h3 style={{ marginTop: 0 }}>
            Team Members ({team?.team?.memberTeams?.length} /{" "}
            {getMemberCount(team, totalSeatsCount)})
          </h3>
          {team?.team?.memberTeams.map((memberTeam, index) => (
            <div
              key={memberTeam?.member?.id}
              style={{
                padding: "0.75rem",
                marginBottom: "0.75rem",
                backgroundColor: "#f9f9f9",
                border: "1px solid #e0e0e0",
                borderRadius: "4px",
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <div>
                <p style={{ margin: "0.25rem 0" }}>Member ID: {memberTeam?.member?.id}</p>
                <p style={{ margin: "0.25rem 0" }}>Member Email: {memberTeam?.member?.email}</p>
                <p style={{ margin: "0.25rem 0" }}>
                  Is Admin: {memberTeam?.isAdmin ? "Yes" : "No"}
                </p>
                <div>
                  <p>Last Login: {dates.getRelativeLoginDateForUser(memberTeam?.member)}</p>
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "1rem",
                  justifyContent: "center",
                }}
              >
                {user.can_access_search && (
                  <div
                    className="buttonContainerAlt"
                    onClick={() => props.openUserFn(memberTeam?.member?.id)}
                  >
                    Go to Member
                  </div>
                )}
                {user.can_access_admin && (
                  <div style={{ alignSelf: "center" }}>
                    {memberTeam?.isAdmin ? (
                      <div
                        className="buttonContainerAlt"
                        onClick={
                          isLoading
                            ? null
                            : (e) => makeAdmin(memberTeam?.member?.id, team?.team?.id, false)
                        }
                      >
                        {isLoading ? (
                          <CircularProgress color="secondary" size={15} />
                        ) : (
                          "Remove Admin"
                        )}
                      </div>
                    ) : (
                      <div
                        className="buttonContainerAlt"
                        onClick={
                          isLoading
                            ? null
                            : (e) => makeAdmin(memberTeam?.member?.id, team?.team?.id, true)
                        }
                      >
                        {isLoading ? (
                          <CircularProgress color="secondary" size={15} />
                        ) : (
                          "Make Admin"
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const addOrderBody = (
    <div>
      <h2 id="simple-modal-title">Team ID: #{team?.team?.id}</h2>
      <p id="simple-modal-description">Owner Email: {team?.team?.email}</p>
      <p>Plan Level: {planLookup(team?.team, price_config)}</p>
      <p>Member Count: {getMemberCount(team, totalSeatsCount)}</p>

      {/* Orders */}
      <h3>Orders</h3>
      <hr />
      <div>
        <p>
          Customs Ordered: <b>{customDevicesOrdered}</b>
        </p>
        <p>
          Standards Ordered: <b>{standardDevicesOrdered}</b>
        </p>
      </div>
      <hr />
      <h3>Details</h3>

      <TextField
        required="true"
        variant="outlined"
        value={shopifyOrderId}
        label="Shopify Order ID"
        onChange={(e) => setShopifyOrderId(e.target.value)}
        disabled={isLoading}
      />
      <br />
      <br />
      <TextField
        required="true"
        variant="outlined"
        value={fulfillOrderId}
        label="Fulfil Order ID"
        onChange={(e) => setFulfillOrderId(e.target.value)}
        disabled={isLoading}
      />

      <h3>Devices</h3>

      <TextField
        required="true"
        variant="outlined"
        value={newStandardDevices}
        type="number"
        label={"New Standard Devices"}
        onChange={(e) => setNewStandardDevices(parseInt(e.target.value, 10))}
        disabled={isLoading}
      />
      <br />
      <br />
      <TextField
        required="true"
        variant="outlined"
        value={newCustomDevices}
        type="number"
        label={"New Custom Devices"}
        onChange={(e) => setNewCustomDevices(parseInt(e.target.value, 10))}
        disabled={isLoading}
      />
      <br />
      <br />

      <div className="buttonContainerAlt" onClick={isLoading ? null : (e) => addOrder()}>
        Add Order
      </div>
      <br />
      <br />
      <div
        className="buttonContainer"
        onClick={isLoading ? null : (e) => setIsAddOrderScreen(false)}
      >
        {isLoading ? <CircularProgress color="secondary" size={15} /> : "Back"}
      </div>
    </div>
  );

  return (
    <Modal open={props.open} onClose={props.closeModal}>
      <div style={modalStyle} className={classes.paper}>
        {isLoading || isCommonActionLoading ? (
          <CircularProgress />
        ) : isAddOrderScreen ? (
          addOrderBody
        ) : (
          body
        )}
      </div>
    </Modal>
  );
}
